import { BridgeComponent } from '@hotwired/hotwire-native-bridge'

const CHECK_INTERVAL = 5 * 60 * 1000
// Par document, donc par webview : survit aux visites Turbo, pas à un rechargement.
let lastCheckAt = 0

// CGU à accepter, sur les pages racines (TermsOfServiceGate, doc/cgu.md) : aux bons
// moments seulement, jamais au milieu d'une action.
//
// - Au chargement : `pending` vient du serveur, qui ne le pose que pour une app qui
//   sait présenter les CGU (bridge-components du user-agent). Le natif présente
//   l'acceptation par-dessus la page (TermsGateComponent, modale plein écran) et décide
//   seul d'où : l'onglet affiché, jamais un onglet préchargé en arrière-plan. Sur le
//   web, le serveur a déjà redirigé.
// - Au retour au premier plan (ou d'un onglet de l'app sélectionné) : la page demande
//   au serveur s'il y en a (checkUrl), au plus une fois par CHECK_INTERVAL — dans l'app,
//   Hotwire Native déplace la webview d'un écran à l'autre à chaque navigation, et
//   chaque déplacement déclenche visibilitychange. Dans l'app, même présentation ;
//   ailleurs, visite de l'acceptation.
//   `enabled` n'est lu qu'ici : au premier chargement, l'adaptateur natif n'est pas
//   encore enregistré, et il vaudrait faux dans l'app aussi.
//
// Une fois la demande faite, `pending` repasse à faux : ni une copie Turbo de la page
// ni un retour arrière ne la répètent. La réponse du natif est attendue pour ça :
// envoyé avant que l'adaptateur soit prêt, le message attend en file, et une
// déconnexion l'effacerait.
export default class extends BridgeComponent {
  static component = 'terms-gate'
  static values = { url: String, checkUrl: String, pending: Boolean }

  // Chargé partout, pas seulement dans une app qui annonce le composant (règle par
  // défaut d'un BridgeComponent) : le retour au premier plan vaut aussi pour le web.
  static get shouldLoad() {
    return true
  }

  connect() {
    super.connect()
    document.addEventListener('visibilitychange', this.#checkIfVisible)
    // Rendue, la page vient d'être vérifiée par le serveur.
    lastCheckAt = Date.now()
    if (this.pendingValue) this.#require()
  }

  disconnect() {
    super.disconnect()
    document.removeEventListener('visibilitychange', this.#checkIfVisible)
  }

  #checkIfVisible = async () => {
    if (document.visibilityState !== 'visible' || Date.now() - lastCheckAt < CHECK_INTERVAL) return

    lastCheckAt = Date.now()
    try {
      const response = await fetch(this.checkUrlValue, { headers: { Accept: 'application/json' } })
      if (!response.ok) return
      const { pending } = await response.json()
      if (!pending) return

      if (this.enabled) {
        this.#require()
      } else {
        this.#visit()
      }
    } catch {
      // Hors-ligne, session expirée (redirection vers la connexion) : prochaine fois.
    }
  }

  #require() {
    const url = new URL(this.urlValue, window.location.href)
    this.send('require', { url: url.href }, () => { this.pendingValue = false })
  }

  #visit() {
    const url = new URL(this.urlValue, window.location.href)
    url.searchParams.set('return_to', window.location.pathname + window.location.search)
    window.Turbo.visit(url.href)
  }
}
